<template>
  <div>
    <Toast group="tr" />
    <!-- popup that is displays the confirmation of updating a location with realisation process -->
    <MapBoxPopup
      v-if="locked && !refId"
      :coordinates="coordinates"
      :offset="[0, -30]"
      :show="true"
      @close="handleClose"
    >
      <div>
        <div class="locked-hint">
          <p class="mb-2">
            <SvgIcon
              class="locked-hint-icon d-inline-block mr-1"
              icon="info-circle-regular"
            />
            <strong> LET OP </strong>
          </p>

          <p class="mb-2">
            {{ $t("components.map.ChargerEditorPopup.confirmRelocate") }}
          </p>

          <p class="mb-1">
            <strong>{{
              $t("components.map.ChargerEditorPopup.dragExplanation")
            }}</strong>
          </p>
          <b-progress
            :value="unlockTimerCountDown"
            :max="unlockTimerDuration"
          />
        </div>
      </div>
    </MapBoxPopup>

    <!-- popup that handles creation and moving of a location in two steps -->
    <ChargerTwoStepGeocodingPopup
      v-else-if="isGeocodingNecessary"
      :is-update="isLocationUpdate"
      :coordinates="coordinates"
      :show="show"
      :first-selected-parking-coordinates="firstSelectedParkingCoordinates"
      :popup-anchor="anchor"
      @create="handleSubmitAdd"
      @update="handleSubmitUpdate"
      @cancel="handleSubmitCancel"
      @close="handleClose"
      @setCoordinates="handleChangeCoordinates({ coordinates: $event })"
      @setAnchor="handleAnchor"
    />

    <!-- popup that is displayed while the sidebar is open -->
    <MapBoxPopup
      v-else-if="show && refId"
      :coordinates="coordinates"
      :offset="[0, -30]"
      :show="show"
      @close="handleClose"
    >
      <div class="d-flex flex-column">
        <div v-if="address">
          {{ address }}
          <ButtonFlyTo :coordinates="coordinates" />
        </div>
        <div>
          {{
            $t(
              `participationPanel.options.statusStations.regularLabels.${statusLabel}`
            )
          }}
        </div>
        <div>CPO: {{ cpo ? cpo.name : $t("none") }}</div>
        <div class="d-flex align-items-center">
          ID: {{ `${code}-${id}` }}
          <i
            v-if="isBoundToProcess"
            v-b-tooltip.hover.right="$t('components.sidebar.lockedTooltip')"
            class="fas fa-lock ml-1 text-warning"
          />
        </div>
        <p v-if="prio && prio.order">
          {{
            $t("components.map.ChargerEditorPopup.dragExplanation", {
              prio: prio.order,
            })
          }}
          {{ prio.fase ? `(${$t("phase")} ${prio.fase})` : "" }}
        </p>
        <!-- todo:: needs to use dataMixin and introducedBY; to be done in: https://ev-it.atlassian.net/browse/TOOLS-969 -->
        <div v-if="user.name">
          <span class="text-muted">{{ user.name }}</span>
        </div>
        <div
          v-if="remark"
          class="mt-3"
        >
          {{ remark }}
        </div>

        <div v-if="features.length > 1">
          <hr class="mt-3 mb-2">

          <b-pagination
            v-model="currentPage"
            class="justify-content-center pagination-sm m-0"
            hide-goto-end-buttons
            :total-rows="features.length"
            :per-page="1"
          />
        </div>
      </div>
    </MapBoxPopup>

    <!-- modal that displays the confirmation of address change -->
    <b-modal
      ref="confirm-move-location-modal"
      title="Locatie verplaatsen"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
    >
      <p>
        {{ $t("components.map.ChargerEditorPopup.confirmAddress") }}
      </p>
      <template #modal-footer>
        <b-button
          variant="light"
          @click="moveCanceled"
        >
          Annuleren
        </b-button>
        <b-button
          variant="secondary"
          @click="moveAndKeepAddress"
        >
          Behouden
        </b-button>
        <b-button
          variant="danger"
          @click="moveAndStartGeocodingProcess"
        >
          Wijzigen
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
/**
 * TODO: Refactor to better quality code
 */

import MapBoxPopup from '@/components/common/MapBoxPopup'
// import { createChargingPoint } from '@/../shared/API/chargingpoint-create'
import { subscribe, unsubscribe } from '@/services/pusher'
import { phaseToArray } from '@/helpers/phases'
import { getLatestCoordinates, setLatestCoordinates } from '@/helpers/chargingpoints'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { EventBus } from '@/services/eventbus'
import { STAKEHOLDER_TYPE } from '@/../shared/valueholders/stakeholder-types'

import chargingpointEditMixin from '@/mixins/chargingpoint/chargingpointEditMixin'
import chargingpointCpoMixin from '@/mixins/chargingpoint/chargingpointCpoMixin'
import chargingpointsLoadMixin from '@/mixins/chargingpoint/chargingpointsLoadMixin'
import userMixin from '@/mixins/common/userMixin'
import { provincieCodeByMunicipalityCode } from '@/services/municipalities'
import SvgIcon from '@/components/common/SvgIcon'
import Toast from 'primevue/toast'

import ChargerTwoStepGeocodingPopup from '@/components/map/modals/ChargerTwoStepGeocodingPopup'
// import { CHARGINGPOINT_STATUS } from '@/../shared/valueholders/chargingpoint-statuses'
import ButtonFlyTo from '@/components/common/ButtonFlyTo.vue'
import { SOURCE } from '@/services/sourceTranslation'

export default {
  name: 'ChargerEditorPopup',
  components: {
    ButtonFlyTo,
    ChargerTwoStepGeocodingPopup,
    SvgIcon,
    MapBoxPopup,
    Toast,
  },
  mixins: [
    chargingpointEditMixin,
    chargingpointsLoadMixin,
    chargingpointCpoMixin,
    userMixin,
  ],
  data() {
    return {
      coordinates: null,

      show: false,

      // Properties (matches ChargerEditorPopup)
      refId: null,
      id: null,
      code: null,
      remark: null,
      user: {
        name: null,
      },
      stakeholders: [],
      status: null,
      address: null,

      validators: [],

      prio: {
        order: null,
        exact: null,
        fase: null,
        customPhase: null,
      },

      // lock if point with fase assigned or is VOLT loc.
      locked: false,

      unlockTimerInterval: null,
      unlockTimerDuration: 4,
      unlockTimerCountDown: null,

      // drag & drop
      draggingPoint: null,

      // if this flag is set, we don't want to deselect the chargingpoint in the store to avoid a flickr,
      // while switching between chargingpoints on the map
      doNotDeselectChargingpoint: false,

      isLocationUpdate: false,
      isLockedForEditing: false,
      isWorkflowCreated: false,

      // pagination
      currentPage: 1,
      features: [],
      firstSelectedParkingCoordinates: null,
      anchor: 'left',
      defaultRemark: 'components.map.ChargerEditorPopup.defaultRemark',
    }
  },
  computed: {
    ...mapGetters('access', ['getActiveMunicipality', 'hasAdminAccess']),
    ...mapGetters('config', [
      'hasValidationSettings',
      'getValidationSettings',
      'getDefaultValidationSettingProfile',
    ]),
    ...mapGetters('planmode', [
      'getChargingPoints',
      'getChargingPointByRefId',
      'getChargingPointByUuid',
      'getPlanmodeLayers',
      'getChargingPointIndexByRefId',
      'isChargingpointSelected',
      'getSelectedChargingpoint',
      'getLockedPhases',
      'getSelectedParkingLotIds',
    ]),
    ...mapGetters('config', ['getPhases']),

    isBraLiMunicipality() {
      return [31, 30].includes(
        provincieCodeByMunicipalityCode({ code: this.getActiveMunicipality }),
      )
    },

    isBoundToProcess() {
      const cp = this.getSelectedChargingpoint
      if (!cp) return false
      return this.workflowActiveLocation({ chargingpoint: cp }) || !!cp.isLockedForEditing
    },

    statusLabel() {
      return this.status
    },

    isGeocodingNecessary() {
      if (!this.show) return false

      if (this.isLocationUpdate) return true

      return !this.refId && this.coordinates
    },

    /**
     * Duplicate ChargingPoint Id check
     *  In edge cases, users may create charging points with the same ID
     *  This computed works together with a watcher to take corrective action when duplicates appear
     */
    duplicateIdUsage() {
      const idMap = {}

      this.getChargingPoints.forEach((item, index) => {
        const id = item.properties.id
        if (!idMap[id]) {
          idMap[id] = []
        }
        idMap[id].push(index)
      })

      const duplicates = Object.values(idMap)
        .filter((indices) => indices.length > 1)
        .map((indices) => indices.map((i) => this.getChargingPoints[i]._id))
      return duplicates
    },

    // Pagination
    visibleChargingPoints() {
      return [this.features[this.currentPage - 1] || false]
    },
    cpo() {
      return this.stakeholders?.find(
        (stakeholder) =>
          !stakeholder.uuid || stakeholder.category === STAKEHOLDER_TYPE.CPO,
      )
    },
  },
  watch: {
    getSelectedChargingpoint: {
      handler(chargingpoint) {
        if (chargingpoint) {
          this.setChargingpointData({ chargingpoint })
        } else {
          this.handleClose()
        }
      },
    },
    // Whenever the chargingpoint is changed by pagination, update the popup
    currentPage() {
      this.handleOpen({ _id: this.visibleChargingPoints[0]?.properties._id })
    },

    /**
     * Hide the popup whenever the municiaplity changes
     */
    getActiveMunicipality: async function (code, previous) {
      this.activatePlanmode()

      this.handleClose()

      // Update Pusher connection
      unsubscribe({ channel: previous })
      let { channel } = await subscribe({ channel: code })
      this.channel = channel
    },

    /**
     * Changes to the pusher channel
     * There are none for this component at this moment
     */
    // channel(channel) {
    //   if (channel === null) return
    // },

    /**
     * Handle the existance of duplicate id usage
     */
    duplicateIdUsage: async function (duplicates) {
      if (duplicates.length === 0) return

      let originalRefId = duplicates[0][0]

      /*
       * check if original chargingpoint still exists, so that only in case of real duplication the id is changed
       * fix for: https://ev-it.atlassian.net/browse/TOOLS-793
       */
      const { chargingpointExists } =
        await this.$_chargingpointEditMixin_exists({
          code: this.getActiveMunicipality,
          refId: originalRefId,
        })

      if (chargingpointExists === false) {
        // if selected
        this.handleClose()

        await this.$_chargingpointsLoadMixin_loadChargingPoints({
          code: this.getActiveMunicipality,
        })

        return
      }

      // Only adjust one chargingpoint ( the last one ). This will change the dataset and trigger the watcher again
      const duplicateID = duplicates[0][duplicates[0].length - 1]

      // TODO: The lambda used to support ID auto-regeneration by omitting `id` from the
      // payload, which triggered getNextId() in the backend. APIv3 has no equivalent for
      // this yet. Until it does, duplicate IDs in this edge case are left unresolved.
      // Track in: https://ev-it.atlassian.net/browse/TOOLS-XXX
      console.warn('[duplicateIdUsage] Duplicate ID detected but auto-reset is not yet supported in APIv3.', duplicateID)
    },

    coordinates() {
      setTimeout(() => {
        if (!this.getSelectedParkingLotIds.length) {
          this.firstSelectedParkingCoordinates = this.coordinates
        }
      })
    },
  },
  /**
   * Bind event handlers to MapBox & connect to Pusher
   */
  created: async function () {
    this.$store.map.on('click', 'chargingpoints', this.handleClickMarkerEvent)
    this.$store.map.on('click', this.handleAddMarkerEvent)

    EventBus.$on('select-chargingpoint', this.handleSelectChargingpointEvent)
    EventBus.$on('deselect-chargingpoint', this.handleClose)

    // Cursor
    this.$store.map.on('mouseenter', 'chargingpoints', this.showPointer)
    this.$store.map.on('mouseleave', 'chargingpoints', this.hidePointer)

    // Drag & drop
    this.$store.map.on('mousedown', 'chargingpoints', this.startDrag)
    this.$store.map.on('touchstart', 'chargingpoints', this.startDrag)

    await this.$_chargingpointEditMixin_connectToPusher()
  },
  /**
   * Disconnect the event handlers from Mapbox
   */
  beforeDestroy() {
    this.$store.map.off('click', 'chargingpoints', this.handleClickMarkerEvent)
    this.$store.map.off('click', this.handleAddMarkerEvent)

    EventBus.$off('select-chargingpoint', this.handleSelectChargingpointEvent)
    EventBus.$off('deselect-chargingpoint', this.handleClose)

    // Cursor
    this.$store.map.off('mouseenter', 'chargingpoints', this.showPointer)
    this.$store.map.off('mouseleave', 'chargingpoints', this.hidePointer)

    this.$store.map.off('mousedown', 'chargingpoints', this.startDrag)
    this.$store.map.off('touchstart', 'chargingpoints', this.startDrag)

    this.$store.dispatch('planmode/setParkingLotIds', { ids: [] })
  },

  /************************************************************************************
   * START COPY & PASTE CODE + minimal modifications
   *  TODO: Review everything below this line...
   */
  methods: {
    ...mapActions('planmode', [
      'selectChargingPoint',
      'deselectChargingPoint',
      'addOrUpdateChargingPoint',
      'activatePlanmode',
      'hideAlternativeLocations',
    ]),
    ...mapMutations('planmode', ['updateChargingpoint']),

    /**
     * Mouse hover effects
     */
    showPointer() {
      this.$store.map.getCanvas().style.cursor = 'pointer'
    },
    hidePointer() {
      this.$store.map.getCanvas().style.cursor = ''

      // cancel unlock popup when mouse leaves point
      this.handleCancel()
    },
    handleChangeCoordinates({ coordinates }) {
      if (coordinates) {
        this.firstSelectedParkingCoordinates = coordinates

        this.$store.map.flyTo({
          center: coordinates,
          zoom: 20,
        })
      } else {
        this.handleClose()
      }
    },
    handleAnchor({ position }) {
      this.anchor = position
    },
    // Check if the location is used in Workflow //
    workflowActiveLocation({ chargingpoint }) {
      if (chargingpoint.isVattenfallProcess) {
        return false
      }
      return (
        !!chargingpoint.workflowCaseRef ||
        !!chargingpoint.workflowUuid ||
        !!chargingpoint.externalProcessId
      )
    },

    /**
     * Drag & Drop
     */
    startUnlockTimer() {
      this.unlockTimerCountDown = this.unlockTimerDuration
      const startedAt = Date.now()

      return new Promise((resolve) => {
        this.unlockTimerInterval = setInterval(() => {
          const time =
            this.unlockTimerDuration - (Date.now() - startedAt) / 1000

          // if negative value, progress-bar won't update properly
          if (time < 0) {
            this.unlockTimerCountDown = 0
          } else {
            this.unlockTimerCountDown = time
          }

          if (this.unlockTimerCountDown <= 0) {
            // give vue time to render the progress-bar to 0 before popup get's removed
            setTimeout(resolve, 500)
          }
        }, 500)
      })
    },

    async startDrag(e) {
      if (!e.features.length) return

      // Prevent the default map drag behavior.
      e.preventDefault()

      this.$store.map.getCanvas().style.cursor = 'grab'
      const [feature] = e.features

      // Get the charging point to get the prio information
      const uuid = feature.properties.uuid
      const chargingpoint = this.getChargingPointByUuid({ uuid })

      this.isWorkflowCreated = this.workflowActiveLocation({ chargingpoint })

      // Location created from Workflow or VOLT or Eco-Movement location can not be moved, trigger notify
      this.isLockedForEditing =
        chargingpoint.isLockedForEditing || this.isWorkflowCreated

      // if chargingpoint was assigned to a fase, then this point should not be edited anymore, so
      // make it harder by locking the point for X seconds
      if (
        phaseToArray(chargingpoint.prio?.fase).some((r) =>
          this.getLockedPhases.includes(r),
        )
      ) {
        this.coordinates = getLatestCoordinates({ chargingpoint })

        this.locked = true

        this.$store.map.on('mouseup', this.handleCancel)

        await this.startUnlockTimer()

        this.$store.map.off('mouseup', this.handleCancel)

        this.unlock()
      }

      this.$store.map.getCanvas().style.cursor = 'grabbing'

      this.$store.map.on('mousemove', this.handleMove)
      this.$store.map.once('mouseup', this.handleUp)
    },

    unlock() {
      clearInterval(this.unlockTimerInterval)
      this.locked = false
    },

    handleCancel() {
      this.unlock()
    },

    /*
     * Programmatically open the location
     */
    handleSelectChargingpointEvent(_id) {
      let chargingpoint = this.getChargingPointByRefId({ _id })

      this.handleOpen({ chargingpoint })
    },

    /**
     * Load the clicked point's properties
     */
    handleClickMarkerEvent(e) {
      if (!e.features.length) return

      this.features = e.features.filter(
        (feature) => feature.layer.paint['icon-opacity'] !== 0,
      )

      // if layer is not visible don't react to clicks
      if (!this.features.length) return

      // Cancel other map events
      e.preventDefault()
      e.originalEvent.stopPropagation()

      // DEBUGGING mapbox features:
      // console.log('event:: ',e.features[0])

      // Get the charging point to get the exact coordinates & user details
      let chargingpoint = this.getChargingPointByRefId({
        _id: this.features[0].properties._id,
      })

      this.$store.map.getCanvas().style.cursor = '' // auto config cursor

      // if there is already a popup opened, set a flag to not deselect the chargingpoint in the store.
      // This is to avoid a flickering of the sidebar, since we need to wait for the nextTick and the current popup is closed

      if (this.isChargingpointSelected && this.show) {
        this.doNotDeselectChargingpoint = true
      }

      this.show = false
      this.$nextTick(() => {
        this.handleOpen({ chargingpoint })

        this.doNotDeselectChargingpoint = false
      })
    },

    /**
     * hydrate with chargingpoint
     */
    setChargingpointData({ chargingpoint }) {
      const caseRef =
        chargingpoint.workflowCaseRef || chargingpoint.externalProcessId
      this.refId = chargingpoint._id
      this.isWorkflowCreated = this.workflowActiveLocation({ chargingpoint })

      const remark = this.isWorkflowCreated
        ? this.$i18n.t('components.sidebar.Tabs.CommentsTab.editNotAllowed', {
            case: caseRef,
          })
        : chargingpoint.properties.remark || this.$i18n.t(this.defaultRemark)

      this.id = chargingpoint.properties.id
      this.code = chargingpoint.code
      this.remark = remark
      this.user.name = chargingpoint.properties.user
        ? chargingpoint.properties.user?.name
        : chargingpoint.properties.user_name || 'EVtools'
      this.stakeholders = chargingpoint.properties.stakeholders || []
      this.status = chargingpoint.properties.status || 'definitive'

      this.address = chargingpoint.address
        ? chargingpoint.address.simple_address
        : ''
      this.validators = chargingpoint.properties.validators || []

      //  If we want to implement the data enrichment we need to rethink this
      this.coordinates = getLatestCoordinates({ chargingpoint })

      this.prio = {
        order: chargingpoint.prio?.order ?? null,
        exact: chargingpoint.prio?.exact ?? null,
        fase: chargingpoint.prio?.fase ?? null,
      }
    },

    /**
     * Upon opening, either by click or event
     */
    handleOpen({ chargingpoint, _id }) {
      if (_id) {
        chargingpoint = this.getChargingPointByRefId({ _id })
      }

      this.setChargingpointData({ chargingpoint })
      this.selectChargingPoint({ _id: chargingpoint._id })

      this.show = true
    },

    /**
     * Upon the close event of the popup
     */
    handleClose() {
      this.show = false
      this.refId = null
      this.firstSelectedParkingCoordinates = null

      if (this.doNotDeselectChargingpoint === false) {
        this.deselectChargingPoint()
      }
    },

    /**
     * Show the form to add a charging point
     */
    handleAddMarkerEvent(e) {
      if (e._defaultPrevented) return

      this.show = false

      this.$nextTick(() => {
        this.id = null
        this.coordinates = e.lngLat.toArray()

        if (!this.firstSelectedParkingCoordinates) {
          this.firstSelectedParkingCoordinates = this.coordinates
        }
        this.show = true
      })
    },

    // this can't work with currently selected chargingpoint, since this is used for drag and drop
    // the chargingpoint needs to be checked beforehand
    getLockedLocationType({ chargingpoint }) {
      if (chargingpoint?.dataSource?.value === SOURCE.ECO_MOVEMENT) {
        return 'Eco-Movement'
      }

      const isWorkflowCreated = this.workflowActiveLocation({ chargingpoint })
      if (isWorkflowCreated) {
        return 'Workflow gecreëerde'
      }

      // TOOLS-3285: Vastzetten palen met fase "Bedrijventerreinen GORAL"
      const customPhase = this.getPhases.find(
        (phase) => phase.uuid === chargingpoint.prio?.customPhase,
      )?.label
      if (customPhase === 'Bedrijventerreinen GORAL') {
        return 'Bedrijventerreinen GORAL'
      }

      return 'RHDHV VOLT'
    },

    /**
     * Track movement
     */
    handleMove(e) {
      // don't allow to drag if there is a popup opened already
      if (this.show) {
        this.$notify({
          type: 'warn',
          title: 'Popup staat al open',
          text: 'Het is niet mogelijk een locatie te verslepen terwijl een popup open staat.',
        })

        this.$store.map.getCanvas().style.cursor = ''
        this.$store.map.off('mousemove', this.handleMove)
        this.$store.map.off('mouseup', this.handleUp)

        return
      }

      if (e._defaultPrevented) {
        this.$store.map.off('mousemove', this.handleMove)
        this.$store.map.off('touchmove', this.handleMove)
        return
      }

      if (this.refId !== null) {
        this.$store.map.off('mousemove', this.handleMove)
        this.$store.map.off('touchmove', this.handleMove)
        return
      }

      let features = this.$store.map.queryRenderedFeatures(e.point, {
        layers: ['chargingpoints'],
      })

      // if not already dragging a point, and no feature is at the cursor, exit
      if (this.draggingPoint === null && features.length === 0) {
        this.$store.map.off('mousemove', this.handleMove)
        this.$store.map.off('touchmove', this.handleMove)
        return null
      }

      if (this.draggingPoint === null) {
        const chargingpoint = this.getChargingPointByRefId({
          _id: features[0].properties._id,
        })

        // don't allow to drag if the location is locked
        const isWorkflowCreated = this.workflowActiveLocation({
          chargingpoint,
        })
        if (chargingpoint.isLockedForEditing || isWorkflowCreated) {
          const type = this.getLockedLocationType({ chargingpoint })

          this.$notify({
            type: 'warn',
            title: `${type} locatie`,
            text: `${type} locatie kan niet worden verplaatst.`,
          })

          this.$store.map.getCanvas().style.cursor = ''
          this.$store.map.off('mousemove', this.handleMove)
          this.$store.map.off('mouseup', this.handleUp)

          return
        }

        // store the point being dragged
        this.draggingPoint = {
          _id: chargingpoint._id,
          coordinates: getLatestCoordinates({ chargingpoint }),
          rollback: structuredClone(chargingpoint),
        }
      }

      // Set a UI indicator for dragging.
      this.$store.map.getCanvas().style.cursor = 'grabbing'

      // Note that we're updating the dataset without persisting the location
      // persisting occurs when the user lets go of the marker
      let coords = e.lngLat

      this.draggingPoint.coordinates = [coords.lng, coords.lat]

      // Make a local update to the chargingpoint collection
      let index = this.getChargingPointIndexByRefId({
        _id: this.draggingPoint._id,
      })
      let chargingpoint = this.getChargingPointByRefId({
        _id: this.draggingPoint._id,
      })
      setLatestCoordinates({ chargingpoint, coordinates: [coords.lng, coords.lat] })
      this.updateChargingpoint({ chargingpoint, index })
    },

    /**
     * End of movement
     */
    async handleUp() {
      this.$store.map.getCanvas().style.cursor = ''

      if (this.draggingPoint === null) {
        this.$store.map.off('mousemove', this.handleMove)
        this.$store.map.off('touchmove', this.handleMove)
        return
      }

      this.showModal()
    },

    showModal() {
      this.$refs['confirm-move-location-modal'].show()
    },
    hideModal() {
      this.$refs['confirm-move-location-modal'].hide()
    },

    moveCanceled() {
      if (!this.draggingPoint) return

      // revert changes by updating the chargingpoint with the rollback data
      this.addOrUpdateChargingPoint({
        chargingpoint: this.draggingPoint.rollback,
      })

      this.stopDragging()
      this.cleanUp()
    },

    moveAndKeepAddress() {
      let chargingpoint = this.getChargingPointByRefId({
        _id: this.draggingPoint._id,
      })

      const [lng, lat] = chargingpoint.coordinates

      this.$_chargingpointEditMixin_save({
        data: {
          uuid: chargingpoint.uuid,
          code: chargingpoint.code,
          lat,
          lng,
          coordinates_overridden: true,
        },
        rollback: this.draggingPoint.rollback,
      })

      this.stopDragging()
      this.cleanUp()
    },

    async moveAndStartGeocodingProcess() {
      let chargingpoint = this.getChargingPointByRefId({
        _id: this.draggingPoint._id,
      })

      this.stopDragging()

      this.coordinates = getLatestCoordinates({ chargingpoint })
      this.isLocationUpdate = true
      this.show = true
    },

    handleSubmitUpdate({ address }) {
      let chargingpoint = this.getChargingPointByRefId({
        _id: this.draggingPoint._id,
      })

      const [lng, lat] = chargingpoint.coordinates

      // positive UI, we expect the API call to be successful, otherwise there will be an error notification and rollback
      this.$_chargingpointEditMixin_save({
        data: {
          uuid: chargingpoint.uuid,
          code: chargingpoint.code,
          lat,
          lng,
          address,
        },
        rollback: this.draggingPoint.rollback,
      })

      this.cleanUp()
    },

    handleSubmitCancel() {
      this.moveCanceled()
      this.cleanUp()
    },

    cleanUp() {
      this.refId = null
      this.coordinates = null
      this.isLocationUpdate = false
      this.show = false
      this.draggingPoint = null
    },

    stopDragging() {
      this.hideModal()

      // Unbind mouse/touch events
      this.$store.map.off('mousemove', this.handleMove)
      this.$store.map.off('touchmove', this.handleMove)
    },

    handleSubmitAdd(chargingpoint) {
      if (chargingpoint.uuid) {
        this.addOrUpdateChargingPoint({
          chargingpoint,
        })

        if (this.channel) {
          this.channel.trigger('client-chargingpoint-saved', {
            chargingpoint,
          })
        }
      } else if (!chargingpoint.uuid && chargingpoint.message.includes('address')) {
          this.$toast.add({
            severity: 'error',
            summary: this.$i18n.t(
              'components.map.ChargerEditorPopup.addressIncomplete.title',
            ),
            detail: this.$i18n.t(
              'components.map.ChargerEditorPopup.addressIncomplete.message',
            ),
            group: 'tr',
            life: 10000,
          })
          return
      } else {
        this.$toast.add({
            severity: 'error',
            summary: this.$i18n.t(
              'components.map.ChargerEditorPopup.defaultPostError.title',
            ),
            detail: this.$i18n.t(
              'components.map.ChargerEditorPopup.defaultPostError.message',
            ),
            group: 'tr',
            life: 15000,
          })
          return
      }

      // Reset parking lot ids (Ams. only)
      this.firstSelectedParkingCoordinates = null
      this.$store.dispatch('planmode/setParkingLotIds', { ids: [] })

      this.show = false
    },
  },
}
</script>

<style lang="scss">
.mapboxgl-popup-content {
  padding: 10px 20px 10px 10px !important;

  p {
    margin: 0;
    user-select: auto;
  }
}

.mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
  border-right-color: var(--primary);
}
.mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
  border-left-color: var(--primary);
}
.mapboxgl-popup-anchor-top .mapboxgl-popup-tip {
  border-bottom-color: var(--primary);
}
.mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip {
  border-top-color: var(--primary);
}
.FormField .invalid-feedback {
  top: 0 !important;
}
.locked-hint {
  font-size: 0.95rem;

  &-icon {
    font-size: 1.2rem;
    color: var(--danger);
  }
}
</style>